// ForkSkillsConfirmDialog — post-fork confirmation that surfaces which
// mastery skills the freshly forked entry will help the user practice.
//
// Why this exists: forks are how users carry community work into their
// own workspace, but the silent toast we used to show didn't tell them
// WHICH parts of the AI Mastery Path the fork actually moves the needle
// on. This dialog splits the source entry's tagged skills into two
// buckets so the value of the fork is visible at a glance:
//
//   • Newly recorded — skills the user has NEVER been credited on.
//     These are the high-leverage ones; tackling the forked playbook
//     will mark them off the path for the first time.
//   • Already demonstrated — skills they've earned credit on before
//     (via any prior playbook). Useful for context ("yes, this fork
//     overlaps with stuff you've done — it's reinforcement, not new
//     ground").
//
// Skill credit lookup happens against `playbook_skill_credits`, which
// is the same table `RecentLibraryExports` reads from, so the dialog's
// "already demonstrated" set always agrees with the /account view.
//
// The dialog is intentionally a CONFIRMATION (info-only) rather than a
// gate — the fork has already happened by the time we open it. The
// primary action ("Open the fork") routes to the redirect path that
// the edge function returned; "Stay here" just dismisses.
import { useEffect, useMemo, useState } from "react";
import {
  Award,
  CheckCircle2,
  ExternalLink,
  GitFork,
  Globe2,
  Loader2,
  Lock,
  Rocket,
  Sparkles,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { AI_MASTERY_PATH, MASTERY_TIERS, type MasterySkill } from "@/data/aiMasteryPath";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { usePublishToLibrary } from "@/hooks/useLibraryMutations";
import { useEntitlements } from "@/hooks/useEntitlements";
import type { LibraryKind } from "@/hooks/useLibrary";
import { toast } from "sonner";
import { retryWithBackoff, isRetryableError } from "@/lib/retryWithBackoff";
import { createRetryToast } from "@/lib/retryToast";
import {
  logForkError,
  formatForkErrorTag,
  newForkFlowId,
} from "@/lib/forkErrorLog";

const SKILL_BY_NUM = new Map(AI_MASTERY_PATH.map((s) => [s.num, s]));

/** Same extraction the /account panel uses, so source skills resolve
 *  consistently no matter which fork surface launched the dialog. */
export function extractEntrySkillNums(
  payload: Record<string, unknown> | null | undefined,
): number[] {
  if (!payload) return [];
  const candidates: unknown[] = [
    payload.skill_nums,
    payload.mastery_skill_nums,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) {
      const nums = c
        .map((v) => (typeof v === "number" ? v : Number(v)))
        .filter((n) => Number.isFinite(n));
      if (nums.length) return Array.from(new Set(nums));
    }
  }
  return [];
}

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Skill numbers tagged on the source Library entry. */
  sourceSkillNums: number[];
  /** Source entry title — used in the dialog header for context. */
  entryTitle: string;
  /** Where "Open the fork" should navigate to (returned by the
   *  library-fork edge function). When omitted, only "Done" shows. */
  redirectPath?: string;
  /** Kind + id of the freshly forked resource. When supplied, the
   *  dialog renders a "Publish to Library" toggle so the user can
   *  immediately turn their fork into either a private working copy
   *  (default) or a public Library entry without leaving the dialog. */
  forkedResourceKind?: LibraryKind;
  forkedResourceId?: string;
  /** Source entry's existing tags — propagated to the new publish so
   *  the published fork inherits discoverability without extra typing. */
  sourceTags?: string[];
}

type Bucket = {
  skill: MasterySkill;
  /** Tier metadata pre-resolved so the row markup stays terse. */
  tier: (typeof MASTERY_TIERS)[keyof typeof MASTERY_TIERS];
};

const SkillRow = ({ skill, tier }: Bucket) => (
  <li className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-border/60 bg-card/50">
    <span className="text-[10px] font-mono text-muted-foreground w-7 shrink-0 tabular-nums">
      #{skill.num}
    </span>
    <span
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-mono uppercase tracking-wider shrink-0",
        tier.pill,
      )}
    >
      <span className={cn("w-1 h-1 rounded-full", tier.dot)} aria-hidden />
      {tier.headline}
    </span>
    <span className="text-[11px] font-mono text-foreground truncate flex-1">
      {skill.label}
    </span>
  </li>
);

const ForkSkillsConfirmDialog = ({
  open,
  onOpenChange,
  sourceSkillNums,
  entryTitle,
  redirectPath,
  forkedResourceKind,
  forkedResourceId,
  sourceTags,
}: Props) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [earnedNums, setEarnedNums] = useState<Set<number> | null>(null);
  const [loading, setLoading] = useState(false);
  // Publish-back state. Default to "private" — the fork is the user's
  // own working copy and we don't want to surprise them by leaking it
  // to the public Library on a single confirmation tap. They can flip
  // to public explicitly if they want to share their version.
  const [publishVisibility, setPublishVisibility] = useState<
    "private" | "public"
  >("private");
  const [publishedOnce, setPublishedOnce] = useState(false);
  // Captured after a successful publish so the footer can render a
  // "View in Library" deep-link to the new entry.
  const [publishedEntryId, setPublishedEntryId] = useState<string | null>(null);
  const publish = usePublishToLibrary();
  const { isPaid, isAdmin } = useEntitlements();
  const canPublishHere =
    Boolean(forkedResourceKind && forkedResourceId) &&
    // protocol_run forks live in a squad and require their own
    // squad-targeted publish flow — keep this dialog scoped to the
    // simple solo kinds where "private vs public" is the right framing.
    forkedResourceKind !== "protocol_run";

  // Reset the publish toggle each time the dialog opens so a previous
  // session's choice doesn't bleed into a new fork.
  useEffect(() => {
    if (open) {
      setPublishVisibility("private");
      setPublishedOnce(false);
      setPublishedEntryId(null);
    }
  }, [open]);

  // Load the user's prior skill credits the first time the dialog
  // opens. We deliberately query EVERY conversation the user has been
  // credited on (not just the freshly forked one) so the bucket split
  // reflects their full history on the AI Mastery Path.
  useEffect(() => {
    if (!open || !user || sourceSkillNums.length === 0) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from("playbook_skill_credits")
        .select("skill_num")
        .eq("user_id", user.id);
      if (cancelled) return;
      const set = new Set<number>(
        (data ?? []).map((r) => Number(r.skill_num)).filter(Number.isFinite),
      );
      setEarnedNums(set);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user, sourceSkillNums]);

  const { newly, already } = useMemo(() => {
    const newly: Bucket[] = [];
    const already: Bucket[] = [];
    if (!earnedNums) return { newly, already };
    for (const num of sourceSkillNums) {
      const skill = SKILL_BY_NUM.get(num);
      if (!skill) continue;
      const tier = MASTERY_TIERS[skill.tier];
      const bucket: Bucket = { skill, tier };
      if (earnedNums.has(num)) already.push(bucket);
      else newly.push(bucket);
    }
    return { newly, already };
  }, [earnedNums, sourceSkillNums]);

  // If the source entry didn't tag any mastery skills AND the parent
  // didn't pass a freshly-forked resource (so the publish toggle has
  // nothing to act on), there's nothing to show — bail and let the
  // toast carry the UX. Otherwise the dialog still serves as the
  // post-fork "publish private/public" surface.
  const hasSkills = sourceSkillNums.length > 0;
  if (!hasSkills && !canPublishHere) return null;

  const handleOpen = () => {
    onOpenChange(false);
    if (redirectPath) navigate(redirectPath);
  };

  const handlePublish = async () => {
    if (!forkedResourceKind || !forkedResourceId) return;
    if (publishVisibility === "private") {
      // Nothing to do — "private" means the fork stays in the user's
      // workspace and is NOT added to the Library.
      return;
    }
    if (publishVisibility === "public" && !isPaid && !isAdmin) {
      toast.error("Public publishing requires Operator. Upgrade to share.");
      return;
    }
    // Stable id so retry pings replace each other instead of stacking.
    const retryToastId = `fork-dialog-pub-${forkedResourceId}`;
    const retryToast = createRetryToast({
      id: retryToastId,
      label: "Publish",
    });
    try {
      // library-publish is idempotent against the same source_id so a
      // retried call after a partial success won't create dupes.
      const publishResult = await retryWithBackoff(
        () =>
          publish.mutateAsync({
            kind: forkedResourceKind,
            source_id: forkedResourceId,
            // Solo publish — no squad target.
            squad_id: null,
            title: `${entryTitle} (my fork)`,
            summary: `Forked from "${entryTitle}".`,
            tags: sourceTags && sourceTags.length > 0 ? sourceTags : undefined,
            visibility: "public",
          }),
        {
          onRetry: retryToast.onRetry,
        },
      );
      retryToast.dismiss();
      setPublishedOnce(true);
      // Cache the new entry id so the post-success toast + the new
      // "View in Library" footer button can deep-link to it. We keep
      // the dialog open so the user can still review their skills.
      setPublishedEntryId(publishResult?.entry?.id ?? null);
      const newId = publishResult?.entry?.id;
      if (newId) {
        toast.success("Published to the Library", {
          action: {
            label: "View in Library",
            onClick: () => {
              onOpenChange(false);
              navigate(`/library?focus=${newId}#entry-${newId}`);
            },
          },
        });
      }
    } catch (err) {
      // Hook already toasted on the FIRST failure (before we wrapped it),
      // but if all retries failed we want a clearer step-aware message
      // with a Retry action. Dismiss the loading toast first.
      retryToast.dismiss();
      // Structured log so the admin error queue captures the publish
      // failure with phase + resource ids + request id. The dialog
      // mints a fresh flow id per attempt because there's no resume
      // contract here — each Retry tap is a new logical flow.
      const logged = logForkError(err, {
        flowId: newForkFlowId("fork-dlg"),
        step: "publish",
        publishedEntryId: forkedResourceId ?? null,
        sourceRef: forkedResourceKind ?? null,
        retryable: isRetryableError(err),
      });
      const raw = err instanceof Error ? err.message : String(err);
      const tagLine = formatForkErrorTag(logged);
      toast.error(
        isRetryableError(err)
          ? "Network hiccup while publishing your fork"
          : "Couldn't publish your fork",
        {
          description: `${raw.slice(0, 160)}\n${tagLine}`,
          duration: 8000,
          action: {
            label: "Retry",
            onClick: () => handlePublish(),
          },
        },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-mono flex items-center gap-2">
            <GitFork className="w-4 h-4 text-primary" />
            {hasSkills ? "Forked — here's the skill impact" : "Forked — what next?"}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">
            {hasSkills ? (
              <>
                "{entryTitle}" tags {sourceSkillNums.length} mastery skill
                {sourceSkillNums.length === 1 ? "" : "s"} on the AI Mastery
                Path. Below is what's new for you and what overlaps with
                credits you've already earned.
              </>
            ) : (
              <>
                Your fork of "{entryTitle}" is ready. Decide whether it
                stays private to your workspace or goes back to the
                Library for others.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1 -mr-1">
          {hasSkills && (loading || !earnedNums) ? (
            <p className="text-[11px] font-mono text-muted-foreground text-center py-6">
              Checking your mastery history…
            </p>
          ) : hasSkills ? (
            <>
              <section>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Sparkles className="w-3 h-3 text-primary" />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-primary">
                    Newly recorded
                  </span>
                  <span className="text-[10px] font-mono text-muted-foreground">
                    {newly.length}
                  </span>
                </div>
                {newly.length === 0 ? (
                  <p className="text-[11px] font-mono text-muted-foreground italic px-1">
                    None — every skill this fork covers is already in
                    your credit history. Solid reinforcement run.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {newly.map((b) => (
                      <SkillRow key={b.skill.num} {...b} />
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400">
                    Already demonstrated
                  </span>
                  <span className="text-[10px] font-mono text-muted-foreground">
                    {already.length}
                  </span>
                </div>
                {already.length === 0 ? (
                  <p className="text-[11px] font-mono text-muted-foreground italic px-1">
                    Fresh territory — none of these skills have been
                    credited to you before.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {already.map((b) => (
                      <SkillRow key={b.skill.num} {...b} />
                    ))}
                  </ul>
                )}
              </section>
            </>
          ) : null}

          {canPublishHere && (
            <section className="border-t border-border/60 pt-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Rocket className="w-3 h-3 text-primary" />
                <span className="text-[10px] font-mono uppercase tracking-wider text-primary">
                  Publish to Library
                </span>
                {publishedOnce && (
                  <span className="text-[10px] font-mono text-primary">
                    ✓ Published
                  </span>
                )}
              </div>
              <p className="text-[11px] font-mono text-muted-foreground mb-2">
                Decide whether your fork stays private or goes back to
                the Library for others to learn from.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setPublishVisibility("private")}
                  disabled={publishedOnce}
                  className={cn(
                    "rounded-md border p-2.5 text-left transition-colors",
                    publishVisibility === "private"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/40",
                    publishedOnce && "opacity-50 cursor-not-allowed",
                  )}
                >
                  <div className="flex items-center gap-1.5 font-mono text-xs font-semibold">
                    <Lock className="w-3 h-3" />
                    Private
                  </div>
                  <p className="text-[10px] font-mono text-muted-foreground mt-0.5">
                    Only in your workspace
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setPublishVisibility("public")}
                  disabled={publishedOnce}
                  className={cn(
                    "rounded-md border p-2.5 text-left transition-colors",
                    publishVisibility === "public"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/40",
                    publishedOnce && "opacity-50 cursor-not-allowed",
                  )}
                >
                  <div className="flex items-center gap-1.5 font-mono text-xs font-semibold">
                    <Globe2 className="w-3 h-3" />
                    Public
                  </div>
                  <p className="text-[10px] font-mono text-muted-foreground mt-0.5">
                    Anyone on HackerLab
                  </p>
                </button>
              </div>
              {publishVisibility === "public" && !isPaid && !isAdmin && (
                <p className="text-[10px] font-mono text-warning mt-1.5">
                  Public publishing requires Operator.
                </p>
              )}
              {publishVisibility === "public" && !publishedOnce && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handlePublish}
                  disabled={publish.isPending}
                  className="font-mono mt-2 w-full"
                >
                  {publish.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Rocket className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Publish my fork publicly
                </Button>
              )}
            </section>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="font-mono"
          >
            Stay here
          </Button>
          {publishedEntryId && (
            <Button
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                navigate(
                  `/library?focus=${publishedEntryId}#entry-${publishedEntryId}`,
                );
              }}
              className="font-mono"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
              View in Library
            </Button>
          )}
          {redirectPath && (
            <Button onClick={handleOpen} className="font-mono">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
              Open the fork
            </Button>
          )}
          {!redirectPath && (
            <Button onClick={() => onOpenChange(false)} className="font-mono">
              <Award className="w-3.5 h-3.5 mr-1.5" />
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ForkSkillsConfirmDialog;